// Regions of a foil card where the white-amplification bloom applies.
// Applying it to the whole card looks bad, so it's masked to the name
// plate (top) and the text box (bottom). Coordinates are fractions
// (0..1) of the card image and were authored with the dev-only
// FoilZoneEditor (`?dev=1&foilzones=1`). Sorcery minions share a frame
// layout, so these relative rects line up across cards.
export const FOIL_WHITE_ZONES = [
  { x: 0.0619, y: 0.0245, w: 0.8786, h: 0.075 },
  { x: 0.0595, y: 0.7133, w: 0.881, h: 0.2455 },
];

// Off-card rect (zero size, far outside 0..1) → that zone slot's shader
// treatment never fires. The shader takes exactly two zones, so single-zone
// frames (sites, avatars) pad their second slot with this.
const OFF_RECT = { x: 2, y: 2, w: 0, h: 0 };

// Off-card zone (both slots dead) → the shader's zone treatment never fires.
export const FOIL_ZONES_OFF = [OFF_RECT, OFF_RECT];

// Avatars are upright (name plate top, rules box bottom). Authored on the
// rules box with FoilZoneEditor (`?dev=1&foilzones=1`).
export const FOIL_AVATAR_ZONES = [
  { x: 0.0857, y: 0.7799, w: 0.8286, h: 0.1789 },
  OFF_RECT,
];

// Sites store the landscape art rotated into a portrait file, so the name
// plate + text box are one vertical strip down the right edge in texture
// space. `rotate90` spins this back to horizontal at display time.
export const FOIL_SITE_ZONES = [
  { x: 0.8214, y: 0.0796, w: 0.1524, h: 0.8388 },
  OFF_RECT,
];

// Pick the foil white-amplification zones for a card by frame type. Spells
// and minions share the vertical spellbook frame; sites and avatars each
// have their own.
export function foilZonesForCard(card) {
  if (!card) return FOIL_WHITE_ZONES;
  const type = card.guardian?.type || card.type;
  if (type === 'Avatar' || card.isAvatar) return FOIL_AVATAR_ZONES;
  if (type === 'Site' || card.isSite || card.played_horizontally) return FOIL_SITE_ZONES;
  return FOIL_WHITE_ZONES;
}

// Build a CSS mask that reveals ONLY the given zones. Each zone is a
// solid (opaque) mask layer sized + positioned to its rect; the default
// add compositing unions them. mask-position uses percentage positioning
// (offset = P * (box - layer)), so the position percent for a zone is
// `coord / (1 - size)`. Returns an inline-style object (both prefixed
// and unprefixed) suitable for spreading onto the bloom layer.
export function foilZoneMaskStyle(zones = FOIL_WHITE_ZONES) {
  const image = zones.map(() => 'linear-gradient(#000, #000)').join(', ');
  const repeat = zones.map(() => 'no-repeat').join(', ');
  const size = zones.map((z) => `${z.w * 100}% ${z.h * 100}%`).join(', ');
  const position = zones
    .map((z) => {
      const px = z.w >= 1 ? 0 : (z.x / (1 - z.w)) * 100;
      const py = z.h >= 1 ? 0 : (z.y / (1 - z.h)) * 100;
      return `${px}% ${py}%`;
    })
    .join(', ');
  return {
    maskImage: image, WebkitMaskImage: image,
    maskRepeat: repeat, WebkitMaskRepeat: repeat,
    maskSize: size, WebkitMaskSize: size,
    maskPosition: position, WebkitMaskPosition: position,
  };
}
